/**
 * React Query hooks for Dishes data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDishes,
  getDishById,
  createDish,
  updateDish,
  deleteDish,
} from '@/lib/api/dishes';
import type { CreateDishRequest, UpdateDishRequest } from '@/types/api';
import { dishKeys } from '@/lib/query-keys';

export { dishKeys };

/**
 * Fetch all dishes
 */
export function useDishes(query?: string) {
  return useQuery({
    queryKey: dishKeys.list(query),
    queryFn: () => getDishes(query),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch a single dish
 */
export function useDish(id: number) {
  return useQuery({
    queryKey: dishKeys.detail(id),
    queryFn: () => getDishById(id),
    enabled: Number.isFinite(id) && id > 0,
  });
}

export function useCreateDish() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDishRequest) => createDish(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dishKeys.all });
    },
  });
}

export function useUpdateDish() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateDishRequest }) =>
      updateDish(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dishKeys.all });
    },
  });
}

export function useDeleteDish() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteDish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dishKeys.all });
    },
  });
}
